import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import { IPartners, IServedZone } from '../../types';
import { alphabeticalSorting } from '../../helpers/utils';

interface AddressFormProps {
  defaultValues: { name: string; surname: string };
  partner?: IPartners;
  onChange: (addressInfo: {
    name: string;
    surname: string;
    address: string;
    note: string;
    city: string;
    rRuleByDay: string;
  }) => void;
}

interface AddressInfo {
  name: string;
  surname: string;
  address: string;
  note: string;
  zone: IServedZone | 'OTHER' | '';
  other: string;
}

const AddressForm: React.FC<AddressFormProps> = ({ partner, defaultValues, onChange }) => {
  const params = {
    ...(partner ? {} : { partners_null: true }),
    pagination: { pageSize: 200 },
  };
  const [{ data: zones = [] }] = useAxios<IServedZone[]>({
    url: `served-zones`,
    params,
  });

  const [addressInfo, setAddressInfo] = useState<AddressInfo>({
    name: `${defaultValues.name}`,
    surname: `${defaultValues.surname}`,
    address: '',
    note: '',
    zone: '',
    other: '',
  });

  useEffect(() => {
    const { name, surname, address, note, zone, other } = addressInfo;
    let city = '';
    let rRuleByDay = '';
    if (typeof zone !== 'string') {
      if (!!zone.rRuleByDay && zone.rRuleByDay !== '') {
        city = zone.name;
        rRuleByDay = zone.rRuleByDay as string;
      } else {
        const { mainZone } = zone;
        city = `${zone.name} (${mainZone?.name})`;
        rRuleByDay = mainZone ? (mainZone.rRuleByDay as string) : '';
      }
    } else {
      city = other;
    }

    onChange({
      name,
      surname,
      address,
      city,
      note,
      rRuleByDay,
    });
  }, [addressInfo]);

  const updateAddressInfoField = (event: any) => {
    if (typeof event.target.value !== 'undefined') {
      setAddressInfo({
        ...addressInfo,
        [event.target.name]: event.target.value,
      });
    }
  };

  const updateAddressInfoCityField = (event: React.ChangeEvent<any>) => {
    const zone = zones.find((zone) => zone.id === event.target.value) || event.target.value;

    if (addressInfo.address === '' && zone?.partners?.length) {
      setAddressInfo({
        ...addressInfo,
        address: zone.name,
        zone,
      });
    } else {
      setAddressInfo({
        ...addressInfo,
        zone,
      });
    }
  };

  const _generateCitySelectItems = () => {
    const elements = [];

    for (const zone of zones.filter((zone) => !zone.mainZone).sort((a, b) => alphabeticalSorting(a.name, b.name))) {
      const sectionTitle = zone.partners?.find((p) => p.id === partner?.id)?.name ?? `Zona di ${zone.name}`;

      elements.push(
        <ListSubheader key={zone.name} disableSticky value={zone.id}>
          {sectionTitle}
        </ListSubheader>
      );

      if (sectionTitle !== partner?.name) {
        elements.push(
          <MenuItem key={zone.id} value={zone.id}>
            {zone.name}
          </MenuItem>
        );
      }

      for (const locality of zones
        .filter((z) => !!z.mainZone && z.mainZone.id === zone.id)
        .sort((a, b) => alphabeticalSorting(a.name, b.name))) {
        elements.push(
          <MenuItem key={locality.id} value={locality.id}>
            {locality.name}
          </MenuItem>
        );
      }
    }

    if (!partner) {
      elements.push(
        <ListSubheader key={'OTHER-LABEL'} disableSticky>
          Altro
        </ListSubheader>
      );
      elements.push(
        <MenuItem key={'OTHER'} value="OTHER">
          Altro
        </MenuItem>
      );
    }

    return elements;
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Indirizzo di consegna
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            name="name"
            label="Nome"
            fullWidth
            variant="outlined"
            value={addressInfo.name}
            onChange={updateAddressInfoField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            name="surname"
            label="Cognome"
            fullWidth
            variant="outlined"
            value={addressInfo.surname}
            onChange={updateAddressInfoField}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="city-label">Zona *</InputLabel>
            <Select
              labelId="city-label"
              name="city"
              value={
                typeof addressInfo.zone === 'undefined'
                  ? ''
                  : typeof addressInfo.zone === 'string'
                  ? addressInfo.zone
                  : addressInfo.zone.id
              }
              onChange={updateAddressInfoCityField}
              inputProps={{
                onInput: (event) => {
                  event.stopPropagation();
                },
              }}
              label="Zona *"
              variant="outlined"
            >
              {_generateCitySelectItems()}
            </Select>
          </FormControl>
        </Grid>
        {addressInfo.zone === 'OTHER' && (
          <Grid item xs={12}>
            <TextField
              required
              name="other"
              label="Indicare il comune o la frazione non presente in lista"
              fullWidth
              variant="outlined"
              value={addressInfo.other}
              onChange={updateAddressInfoField}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            required
            name="address"
            label="Indirizzo"
            fullWidth
            variant="outlined"
            value={addressInfo.address}
            onChange={updateAddressInfoField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="note"
            label="Note"
            fullWidth
            variant="outlined"
            value={addressInfo.note}
            onChange={updateAddressInfoField}
            multiline
            rows={4}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AddressForm;
